import React, { useState } from 'react';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { v4 as uuidv4 } from 'uuid';
import { storage, auth } from '../firebase';
import { 
  Button, 
  CircularProgress, 
  Box, 
  Typography, 
  Paper, 
  IconButton,
  Stack,
  Alert
} from '@mui/material';
import { CloudUpload, InsertDriveFile, Delete } from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

interface InsuranceDocumentUploaderProps {
  onUploadComplete: (documentData: {
    url: string;
    fileName: string;
    uploadedAt: Date;
  }) => void;
  onRemove?: () => void;
  currentDocument?: {
    url: string;
    fileName: string;
  };
}

export const InsuranceDocumentUploader: React.FC<InsuranceDocumentUploaderProps> = ({
  onUploadComplete,
  onRemove,
  currentDocument
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [document, setDocument] = useState(currentDocument);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Check if user is authenticated
    const currentUser = auth.currentUser;
    if (!currentUser) {
      setError("You must be logged in to upload files");
      return;
    }

    // Validate file type
    const validFileTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "image/jpeg",
      "image/png"
    ];

    if (!validFileTypes.includes(file.type)) {
      setError("File type not supported. Allowed types: PDF, Word, JPEG, PNG");
      return;
    }

    // Validate file size (5MB max)
    const MAX_SIZE = 5 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      setError("File size exceeds 5MB limit");
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      // Create a unique filename
      const fileExtension = file.name.split(".").pop() || "";
      const fileName = `${uuidv4()}.${fileExtension}`;

      // Generate temporary vendorId if not yet created
      const tempVendorId = "temp_" + currentUser.uid;
      
      // Set storage path and reference
      const storagePath = `vendor_insurance/${tempVendorId}/${fileName}`;
      const storageRef = ref(storage, storagePath);

      // Add metadata
      const metadata = {
        contentType: file.type,
        customMetadata: {
          uploadedBy: currentUser.uid,
          uploadedAt: new Date().toISOString(),
          originalFilename: file.name,
          documentType: "insurance"
        },
      };

      // Upload file with progress tracking
      const uploadTask = uploadBytesResumable(storageRef, file, metadata);

      uploadTask.on(
        "state_changed",
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setProgress(Math.round(progress));
        },
        (error) => {
          setIsUploading(false);
          setError("Upload failed: " + error.message);
        },
        async () => {
          // Upload completed successfully
          const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref);
          
          const documentData = {
            url: downloadUrl,
            fileName: file.name,
            uploadedAt: new Date(),
          };
          
          setDocument({
            url: downloadUrl,
            fileName: file.name
          });
          
          onUploadComplete(documentData);
          setIsUploading(false);
        }
      );
    } catch (err) {
      setIsUploading(false);
      setError("Upload failed: " + (err as Error).message);
    }
  };

  const handleRemoveDocument = () => {
    setDocument(undefined);
    if (onRemove) {
      onRemove();
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 2,
        border: '1px dashed',
        borderColor: 'divider',
        bgcolor: 'background.default'
      }}
    >
      <Typography variant="subtitle1" gutterBottom fontWeight="medium">
        Insurance Document <Box component="span" sx={{ color: 'error.main' }}>*</Box>
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Upload your business insurance document (PDF, Word, JPEG, PNG). Max size: 5MB. Required for registration.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {document ? (
        <Box sx={{ mt: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <InsertDriveFile color="primary" />
            <Typography variant="body2" sx={{ flexGrow: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {document.fileName}
            </Typography>
            <IconButton size="small" onClick={handleRemoveDocument}>
              <Delete fontSize="small" />
            </IconButton>
          </Stack>
        </Box>
      ) : isUploading ? (
        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
          <CircularProgress size={20} sx={{ mr: 1 }} />
          <Typography variant="body2">{progress}% Uploading...</Typography>
        </Box>
      ) : (
        <Button
          component="label"
          variant="outlined"
          startIcon={<CloudUpload />}
          sx={{ mt: 2 }}
          disabled={isUploading}
        >
          Upload Insurance Document
          <VisuallyHiddenInput 
            type="file" 
            onChange={handleFileUpload}
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
          />
        </Button>
      )}
    </Paper>
  );
}; 